import React, { useEffect, useRef, useState } from "react";
import { useLingui } from "@lingui/react";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import usFlag from "../assets/flag-us.svg";
import plFlag from "../assets/flag-pl.svg";
import ilFlag from "../assets/flag-il.svg";
import { applyDocumentLocale, isUiLocale, storeLocale, type UiLocale } from "../utils/locale";
import "../App.css";

const languages: ReadonlyArray<{ locale: UiLocale; label: string; flag: string }> = [
  { locale: "en", label: "English", flag: usFlag },
  { locale: "pl", label: "Polski", flag: plFlag },
  { locale: "he", label: "עברית", flag: ilFlag },
];

const LanguageSwitcher: React.FC = () => {
  const { i18n: linguiI18n } = useLingui();
  const [isOpen, setIsOpen] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const closeMenu = (event: MouseEvent) => {
      if (!switcherRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", closeMenu);
    return () => document.removeEventListener("mousedown", closeMenu);
  }, []);

  const changeLanguage = (locale: UiLocale) => {
    storeLocale(locale);
    linguiI18n.activate(locale);
    applyDocumentLocale(locale);
    setIsOpen(false);
  };

  const selectLocale = (locale: string): void => {
    if (isUiLocale(locale)) {
      changeLanguage(locale);
    }
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      setIsOpen(false);
    }
  };

  return (
    <div className="language-switcher" ref={switcherRef}>
      <button
        type="button"
        className="language-menu-button"
        onClick={() => setIsOpen((open) => !open)}
        aria-label="Select language"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls="language-menu"
      >
        <LanguageRoundedIcon aria-hidden="true" />
      </button>

      {isOpen && (
        <div id="language-menu" className="language-menu" role="menu" onKeyDown={handleMenuKeyDown}>
          {languages.map(({ locale, label, flag }) => {
            const isActive = linguiI18n.locale === locale;

            return (
              <button
                key={locale}
                type="button"
                onClick={() => selectLocale(locale)}
                className={`language-button ${isActive ? "active" : ""}`}
                role="menuitemradio"
                aria-checked={isActive}
                lang={locale}
              >
                <img src={flag} width={18} height={13} alt="" aria-hidden="true" />
                <span className="language-button-label">{label}</span>
                {isActive && <CheckRoundedIcon className="language-selected-icon" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
